import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { assignUserToTheJob, manageKanbanJobResponsibles } from '../../Services/jobService';
import { assignUserToTheApplicant } from '../../Services/applicantService';
import { assignUserToTheApplication } from '../../Services/applicationService';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import FormSelect from '../ReactHookFormCustom/FormSelect';
import FormCheckBox from '../ReactHookFormCustom/FormCheckBox';
import useUsersStore from '../../../../stores/usersStore';

export const AssignedUserType = {
  AssignedToJob: 'Job',
  AssignedToApplicant: 'Applicant',
  AssignedToApplication: 'Application',
  AssignedToKanbanColumn: 'KanbanColumn',
};

const AssignedUserModal = ({
  showModal,
  onToggle,
  onStatusRefresh,
  assignedUserType,
  job,
  jobId,
  application,
  applicant,
  selectedColumn,
  required = true,
  preselectedOptions = [], // <-- array of existing responsibles
}) => {
  const { t } = useTranslation();
  const users = useUsersStore((state) => state.users);
  const [isLoading, setIsLoading] = useState(false);

  // Build react-select options
  const composeUserName = useCallback((u) => `${u.firstName} ${u.lastName} (${u.username})`, []);
  const usersOptions = useMemo(
    () => (users || []).filter((u) => u.status === 'Active').map((u) => ({ value: u.id, label: composeUserName(u) })),
    [users, composeUserName]
  );

  const initialKanbanSelection = useMemo(() => {
    return preselectedOptions?.map((user) => {
      const foundUser = usersOptions.find((u) => u.value === user.userId);
      return foundUser ? { value: foundUser.value, label: foundUser.label, settingId: user.id } : null;
    });
  }, [preselectedOptions]);

  // Set up RHF, seeding in your initial selection
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      selectedUser: initialKanbanSelection,
      assignAlsoApplications: false,
    },
  });

  // Whenever modal opens, re-seed the form with the latest prop
  useEffect(() => {
    if (showModal && initialKanbanSelection) {
      reset({
        selectedUser: initialKanbanSelection,
        assignAlsoApplications: false,
      });
    }
  }, [showModal, reset]);

  const onSubmit = useCallback(
    async (values) => {
      try {
        setIsLoading(true);
        let response;
        switch (assignedUserType) {
          case AssignedUserType.AssignedToJob:
            response = await assignUserToTheJob(job.id, values.selectedUser?.value, values.assignAlsoApplications);
            break;

          case AssignedUserType.AssignedToApplication:
            response = await assignUserToTheApplication(application.applicationId, values.selectedUser?.value);
            break;

          case AssignedUserType.AssignedToApplicant:
            response = await assignUserToTheApplicant(
              applicant.id,
              values.selectedUser?.value,
              values.assignAlsoApplications
            );
            break;

          case AssignedUserType.AssignedToKanbanColumn:
            if (!jobId) throw new Error('Job ID is required for Kanban assignment');

            const dtos = (values.selectedUser || []).map((u) => ({
              Id: u.settingId,
              JobId: jobId,
              UserId: u.value,
              KanbanColumnId: selectedColumn.id,
            }));

            response = await manageKanbanJobResponsibles(
              dtos.length > 0
                ? dtos
                : [
                    {
                      Id: 0,
                      JobId: jobId,
                      UserId: 0,
                      KanbanColumnId: selectedColumn.id,
                    },
                  ]
            );
            break;

          default:
            throw new Error('Invalid assignment type');
        }

        setIsLoading(false);
        if (response.status === 200) {
          onToggle();
          onStatusRefresh();
        }
      } catch (err) {
        setIsLoading(false);
        console.error('Assignment error:', err);
      }
    },
    [assignedUserType, job, jobId, application, applicant, selectedColumn, onToggle, onStatusRefresh]
  );

  return (
    <Modal isOpen={showModal} centered>
      <ModalHeader>
        <em className="fa-2x icon-user mr-2">
          {assignedUserType === AssignedUserType.AssignedToKanbanColumn
            ? t('kanban.MANAGE_RESPONSIBLE')
            : t('jobs.CHANGE_ASSIGNED_TO')}
        </em>
      </ModalHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Row>
            <Col>
              <FormSelect
                name="selectedUser"
                id="selectedUserId"
                control={control}
                label={t('competences.NAME')}
                options={usersOptions}
                isMulti={assignedUserType === AssignedUserType.AssignedToKanbanColumn}
                closeMenuOnSelect={assignedUserType !== AssignedUserType.AssignedToKanbanColumn}
                rules={required ? { required: t('common.REQUIRED') } : {}}
                // NOTE: RHF’s reset call takes care of defaultValue for Controller
              />
            </Col>
          </Row>

          {[AssignedUserType.AssignedToJob, AssignedUserType.AssignedToApplicant].includes(assignedUserType) && (
            <>
              <br />
              <Row>
                <Col>
                  <FormCheckBox
                    name="assignAlsoApplications"
                    id="assignAlsoApplicationsId"
                    control={control}
                    label={t(`assignUserModal.ALSO_ASSIGN_APPLICATIONS_FROM_${assignedUserType.toUpperCase()}`)}
                  />
                </Col>
              </Row>
            </>
          )}

          {assignedUserType === AssignedUserType.AssignedToKanbanColumn && (
            <Row>
              <Col>
                <p className="text-muted">{t('kanban.AUTOMATED_EMAIL')}</p>
              </Col>
            </Row>
          )}
        </ModalBody>

        <ModalFooter>
          <Button type="submit" color="primary" className="mr-1">
            {isLoading && <div className="spinner-border spinner-border-sm mr-1" role="status" />}
            {t('common.SAVE')}
          </Button>
          <Button color="secondary" onClick={onToggle}>
            {t('common.CANCEL')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AssignedUserModal;
